import {
  Col,
  MenuProps,
  Row,
  Spin,
  Typography,
  Tooltip as AntdTooltip,
} from 'antd';
import { DownloadKeys, DownloadLoader } from 'enums/GraphDropdownEnums';
import { SourceCO2eData } from 'models/MainDashboard/emissionModelSource.model';
import React, { FC, useEffect, useRef, useState } from 'react';
import { PieChart, Pie, Tooltip, Cell, TooltipProps } from 'recharts';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { downloadGraph } from 'shared/utils/downloadStaticGraph';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import {
  COLORS,
  CompanyDashboardProps,
  customFillColor,
  customStyle,
  dataKeyPercentage,
  ScopeProps,
  SourceColors,
} from '../constants';
import styles from './piechartsource.module.scss';
import { EmissionData } from 'models/MainDashboard/emissionModel.model';

//GuardCheck
const isEmissionData = (
  entry: EmissionData | SourceCO2eData
): entry is EmissionData => 'scope' in entry;

const PieChartSource: FC<ScopeProps> = ({
  sourceData,
  scopeData,
  year,
  orgId,
  scopeCategory,
  locationId,
  fetchEmissionDataSource,
  loading,
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isTooltipReady, setIsTooltipReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTooltipReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const isScopeEmission = scopeCategory === CompanyDashboardProps.ALL;

  const onPieEnter = (_: string, index: number) => {
    if (isTooltipReady) {
      setActiveIndex(index);
    }
  };
  const handleDownload: MenuProps['onClick'] = async ({ key }) => {
    await downloadGraph(
      key,
      graphRef,
      isScopeEmission
        ? CompanyDashboardProps.EMISSIONSCOPE
        : CompanyDashboardProps.EMISSIONCHART,
      setIsDownloading
    );
    if (key === DownloadKeys.CSV) {
      const fetchParams = {
        year,
        organizationId: orgId,
        ...(!isScopeEmission && { scopeCategory }),
        exportType: DownloadKeys.CSV,
        ...(locationId !== CompanyDashboardProps.ALL &&
          !!locationId && { locationId: Number(locationId) }),
      };

      fetchEmissionDataSource(fetchParams);
    }
  };

  const sortedData = scopeData?.scopeEmission?.sort((a, b) => {
    return (b?.percentage ?? 0) - (a?.percentage ?? 0);
  });

  const renderTooltipContent = (tooltipProps: TooltipProps<string, string>) => {
    const { payload } = tooltipProps;
    return payload && payload.length ? (
      <CustomTooltip
        isPie
        isPieSource={!isScopeEmission}
        payload={payload[0]?.payload.payload}
      />
    ) : null;
  };

  const getSpanValue = (data?: EmissionData[] | SourceCO2eData[]) => {
    switch (data?.length) {
      case 4:
      case 2:
        return { span: 12 };
      case 6:
      case 5:
      case 3:
        return { span: 8, startAlign: true };
      default:
        return { span: 24 };
    }
  };

  const pieChartData = () => {
    const data = isScopeEmission ? sortedData : sourceData?.sourceCO2e;
    const { span, startAlign } = getSpanValue(data);

    return {
      data,
      keyLegends: (
        <Row
          gutter={[10, 10]}
          align="middle"
          className={styles.legendsWrapper}
          justify={'center'}>
          {data?.map((entry, index) => {
            const name = isEmissionData(entry) ? entry?.scope : entry?.fuelName;
            return entry.percentage ? (
              <Col span={span} key={`legend ${index}`}>
                <Row
                  align="middle"
                  justify={!isScopeEmission && startAlign ? 'start' : 'center'}>
                  <Col className={styles[`scope${index + 1}`]}></Col>
                  <AntdTooltip title={name}>
                    <Typography.Paragraph
                      ellipsis={{ rows: 1 }}
                      className={styles.label}>
                      {name}
                    </Typography.Paragraph>
                  </AntdTooltip>
                  <Col className={styles.percentage}>
                    {entry.percentage + CompanyDashboardProps.PERC}
                  </Col>
                </Row>
              </Col>
            ) : null;
          })}
        </Row>
      ),
    };
  };

  const { data, keyLegends } = pieChartData();

  const totalPercentage = isScopeEmission
    ? sortedData?.reduce((sum, entry) => {
        return sum + (entry?.percentage || 0);
      }, 0)
    : sourceData?.sourceCO2e?.reduce<number>(
        (acc, curr) => acc + (curr.percentage ?? 0),
        0
      );

  const isPercentageZero = totalPercentage === 0;
  return (
    <>
      {!isPercentageZero ? (
        <Spin
          spinning={isDownloading || loading}
          {...(isDownloading && { tip: DownloadLoader.DOWNLOADING })}>
          <div className={styles.scopePiechart}>
            <div className={styles.positionedDiv}>
              <GraphDropDown
                handleDownload={handleDownload}
                showSort={false}
                isMainDashboard
                graphRef={graphRef}
                setIsDownloading={setIsDownloading}
                isSort
              />
            </div>
            <div ref={graphRef} className={styles.itemsContainer}>
              <div className={styles.itemsContainer}>
                <PieChart width={250} height={240}>
                  <Pie
                    activeIndex={activeIndex}
                    data={data}
                    dataKey={dataKeyPercentage}
                    outerRadius={120}
                    fill={customFillColor}
                    onMouseEnter={onPieEnter}
                    style={customStyle}>
                    {data?.map((_: SourceCO2eData, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          isScopeEmission
                            ? COLORS[index % COLORS.length]
                            : SourceColors[index % SourceColors.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    cursor={false}
                    content={isTooltipReady ? renderTooltipContent : undefined}
                    isAnimationActive={isTooltipReady}
                  />
                </PieChart>
                {keyLegends}
              </div>
            </div>
          </div>
        </Spin>
      ) : (
        <p>{CompanyDashboardProps.DATA_EMPTY}</p>
      )}
    </>
  );
};

export default PieChartSource;
